import { useState } from 'react';
import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import { useAuth0 } from '@auth0/auth0-react';
import { useHandleError } from './useHandleError';
import { ErrorMessages } from '../types/ErrorMessages';
import { ApiError } from '../types/ApiError';
import { withoutLengthText } from '../utils/withoutLengthText';
import { ErrorTypes } from '../types/backendResponses/ErrorTypes';

/**
 * A custom hook for making API calls using Axios.
 *
 * @template T - The type of the response data.
 * @param {string} url - The endpoint to call (relative to the API host).
 * @param {AxiosRequestConfig} [defaultOptions] - Default Axios request configuration.
 * @param {ErrorTypes[]} [expectedErrors] - Error statuses the caller handles itself, so no alert is shown for them.
 * @returns {object} An object containing the API response data, loading state, error state, and a `callAPI` function.
 */
export default function useAPI<T>(
  defaultOptions?: AxiosRequestConfig,
  expectedErrors: ErrorTypes[] = []
) {
  const { handleError } = useHandleError();

  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();

  // Base API host URL (configured via environment variables)
  const host = import.meta.env.VITE_REACT_APP_API_URL;

  // State for storing the API response data
  const [data, setData] = useState<T | null>(null);

  // State for tracking the loading status
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // State for tracking any errors that occur during the API call
  const [error, setError] = useState<ApiError | null | AxiosError>(null);

  /**
   * Function to make an API call to the given URL.
   *
   * @param {object} [body] - Optional body to send with the request.
   * @param {AxiosRequestConfig} [overrideOptions] - Optional additional Axios configuration.
   */
  const callAPI = async (
    url: string,
    body?: object | null,
    errorMessages?: ErrorMessages,
    overrideOptions?: AxiosRequestConfig
  ) => {
    setData(null); // reset the data when calling

    const token = await getTokenOrLogIn();

    setIsLoading(true); // Indicate the request is in progress
    try {
      const response: AxiosResponse<T> = await axios({
        ...defaultOptions,
        ...overrideOptions,
        url: host + url,
        data: withoutLengthText(body),
        headers: {
          Authorization: `Bearer ${token}`,
          ...defaultOptions?.headers,
          ...overrideOptions?.headers, // Merge headers dynamically
        },
      });
      setData(response.data); // Store the response data

      if (defaultOptions?.method === 'POST' || defaultOptions?.method === 'PUT') {
        return response.data;
      }
      return response.data;
    } catch (e) {
      const error = e as ApiError;
      // A request for a file gets its error body as a Blob too; the status is read from its JSON.
      if (error?.response?.data instanceof Blob) {
        try {
          error.response.data = JSON.parse(await error.response.data.text());
        } catch {
          // Not JSON, such as an error page from the load balancer; the generic message is shown.
        }
      }

      if (!expectedErrors.includes(error?.response?.data?.status)) {
        handleError(error, errorMessages);
      }
      setError(error); // Store the error if the request fails
      throw error as AxiosError;
    } finally {
      setIsLoading(false); // Indicate the request is complete
    }
  };

  const getTokenOrLogIn = async () => {
    try {
      return await getAccessTokenSilently();
    } catch (e) {
      // The cached ID token keeps isAuthenticated true after the session has expired, so
      // nothing else sends the user back to the login page.
      loginWithRedirect({ appState: { returnTo: window.location.pathname } });
      throw e;
    }
  };

  // Return the API response data, loading state, error state, and the API call function
  return { data, isLoading, error, callAPI };
}
